import React, { useEffect } from 'react';

import { track } from '@betterment/js-runtime';
import {
  Box,
  Button,
  CenteredWorkflowLayout,
  ErrorText,
  Heading,
  InputGroup,
  Label,
} from '@betterment/style-closet-react';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { DirectDepositStepProps } from './data';
import { useTitleDispatch } from './utils';

import { FormikNumberInput } from '../../../Shared/components/NumberInput';

interface FormProps {
  value: number;
}

const trackSubmitClick = () => track('ElementClicked', { name: 'SaveAmountofPaycheck', module: 'SetUpDirectDeposit' });

const PercentageFormSchema = Yup.object().shape<Partial<{}>>({
  value: Yup.number()
    .required('Percentage cannot be blank.')
    .test('value', 'Percentage cannot exceed 100%.', (v: number) => v <= 100)
    .test('value', 'Percentage must be greater than 0%.', (v: number) => v > 0)
    .test('value', 'Percentage must be a whole number', (v: number) => v % 1 === 0),
});

export const PayCheckPercentageField: React.FC<React.PropsWithChildren<DirectDepositStepProps>> = ({
  flowState,
  goToNextStep,
}) => {
  const setTitle = useTitleDispatch();
  const processForm = (formValues: FormProps) => {
    goToNextStep({ payCheckAmount: { type: 'percentage', value: formValues.value } });
  };

  let { payCheckAmount } = flowState.values;
  if (!payCheckAmount) payCheckAmount = { type: 'percentage', value: 100 };
  if (payCheckAmount.type !== 'percentage') {
    payCheckAmount.value = 100;
  }
  useEffect(() => setTitle('Direct deposit form'), [setTitle]);
  return (
    <>
      <Heading variant="primary">What percentage of each paycheck do you want to deposit?</Heading>
      <Box mt={8}>
        <Formik
          onSubmit={processForm}
          validationSchema={PercentageFormSchema}
          initialValues={{ value: payCheckAmount.value }}
          validateOnBlur={false}
          enableReinitialize={true}
        >
          {({ isSubmitting }) => (
            <Form>
              <InputGroup>
                <Label id="value-label" htmlFor="value">
                  Percentage
                </Label>
                <FormikNumberInput
                  name="value"
                  id="value"
                  aria-labelledby="value-label"
                  aria-describedby="value-error"
                  unit="percent"
                />
                <ErrorMessage name="value">{msg => <ErrorText id="value-error">{msg}</ErrorText>}</ErrorMessage>
              </InputGroup>
              <CenteredWorkflowLayout.Actions>
                <Button type="submit" disabled={isSubmitting} onClick={trackSubmitClick}>
                  Save
                </Button>
              </CenteredWorkflowLayout.Actions>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};
